import React, { memo, useEffect, useContext } from 'react'
import { LngLatBounds } from 'maplibre-gl/dist/maplibre-gl'
import tilebelt from '@mapbox/tilebelt'

import settings from '../../conf'
import MapContext from './MapContext'
import Loading from '../Loading'
import { ICON_MAPPING } from '../Icons'

import { amenitiesIconsText, travelLength } from '../../utils'
import { layers as stepLayers } from './styles/steps.js'
import { layers as routeLayers } from './styles/route.js'

import { useHasMapFeatures } from '../../slices/map'
import { useSettings } from '../../slices/settings'

import {
  useSiteMapsetting,
  useRouteMapsettings,
  useRoute,
  useSteps

} from '../../api'

export const buildiMatchExpression = (keys, values, fallback) => {
  const expression = ['match', ['get', 'step_uid']]
  for (const id in keys) {
    expression.push(keys[id])
    expression.push(values[id])
  }
  expression.push(fallback)
  return expression
}

const removeRouteSource = (map) => {
  if (!map || !map.style || !map.getSource('route')) return

  for (const layer of routeLayers(null)) {
    if (map.getLayer(layer.id)) map.removeLayer(layer.id)
  }

  for (const layer of stepLayers(null)) {
    if (map.getLayer(layer.id)) map.removeLayer(layer.id)
  }
  map.removeSource('route')
  map.setFilter('place', null)
  map.setFilter('icon', null)
}

const addRouteSource = (map, routeMapSettings) => {
  const quadkeys = routeMapSettings.tiles.map(tileid => tilebelt.tileToQuadkey(tileid))

  map.addSource('route', {
    type: 'vector',
    tiles: [
      routeMapSettings.source_url
    ],
    minzoom: 4,
    maxzoom: 16
  })

  const routeSource = map.getSource('route')

  routeSource.tileBounds = {
    contains: (tileid) => {
      const quadkey = tilebelt.tileToQuadkey([tileid.x, tileid.y, tileid.z])
      for (const q of quadkeys) {
        const len = Math.min(quadkey.length, q.length)
        if (q.substring(0, len) === quadkey.substring(0, len)) {
          return true
        }
      }
      return false
    }
  }

  return routeSource
}

const RouteLayer = memo((props) => {
  const { site_id = settings.SITE_ID, route_id, trail_id, highlight_id, offsetDistance } = props
  const map = useContext(MapContext)
  const { zoom, unit } = useSettings()
  const siteMapSettings = useSiteMapsetting({ siteId: site_id })
  const routeMapSettings = useRouteMapsettings({ siteId: site_id, routeId: route_id })
  const route = useRoute({ siteId: site_id, routeId: route_id })
  const steps = useSteps({ siteId: site_id, routeId: route_id, trailId: trail_id }, { skip: !trail_id })
  const hasFeatures = useHasMapFeatures()
  const trail_highlight_id = highlight_id || trail_id

  useEffect(() => {
    if (!map) return
    removeRouteSource(map)
    if (routeMapSettings) {
      addRouteSource(map, routeMapSettings)
    }
    return () => map && removeRouteSource(map)
  }, [map, routeMapSettings])

  useEffect(() => {
    if (!map) return

    for (const layer of routeLayers(zoom)) {
      if (map.getLayer(layer.id)) map.removeLayer(layer.id)
      if (routeMapSettings) map.addLayer(layer, 'place')
    }

    for (const layer of stepLayers(zoom)) {
      if (map.getLayer(layer.id)) map.removeLayer(layer.id)
      if (routeMapSettings) map.addLayer(layer, 'place')
    }

    const e = routeMapSettings ? routeMapSettings.extent : siteMapSettings.extent
    if (e) {
      map.maxBounds = new LngLatBounds([
        [e[0], e[1]],
        [e[2], e[3]]
      ])
    }
  }, [map, zoom, routeMapSettings, siteMapSettings])

  useEffect(() => {
    if (!map || !routeMapSettings || !route || (!steps && trail_id)) return

    const options = {
      validate: settings.DEBUG
    }

    const quadhashs = []
    const markerUid = []
    const startStepUid = []
    const startStopStepUid = []
    const startStopLabel = []

    for (const trail of route.trails) {
      for (const [waypoint, step_id] of [[trail.start_place, 1], [trail.stop_place, trail.steps_count]]) {
        const uid = `${trail.trail_id}-${step_id}`
        if (trail.trail_id !== 1 && step_id === 1) {
          startStepUid.push(uid)
        }
        if (quadhashs.indexOf(waypoint.quadhash) === -1) {
          quadhashs.push(waypoint.quadhash)
          markerUid.push(uid)
          startStopStepUid.push(uid)
          startStopLabel.push(waypoint.title)
        }
      }
    }

    if (steps) {
      const stepLabelUid = []
      const stepLabel = []
      const stepDistanceLabelUid = []
      const stepDistanceLabel = []
      const stepDistanceOffsetUid = []
      const stepDistanceOffset = []
      const iconQuadhashs = []
      const iconUid = []
      const iconLabel = []

      for (const step of steps) {
        const uid = `${trail_id}-${step.step_id}`
        const offset = Object.keys(step.amenities).filter(x => x in ICON_MAPPING).length
        const length = (offsetDistance) ? Math.abs(step.start_length - offsetDistance) : step.start_length

        if (!step.is_deviation) {
          stepDistanceLabelUid.push(uid)
          stepDistanceLabel.push(travelLength(length, unit))

          if (offset > 0) {
            stepDistanceOffsetUid.push(uid)
            stepDistanceOffset.push(['literal', [1 + (offset * zoom) / 1.2, 0]])
          }
        }

        if (offset > 0 && step.waypoint_quadhashs.some(x => iconQuadhashs.indexOf(x) === -1)) {
          for (const quadhash of step.waypoint_quadhashs) {
            if (iconQuadhashs.indexOf(quadhash) === -1) {
              iconQuadhashs.push(quadhash)
            }
          }
          iconUid.push(uid)
          iconLabel.push(amenitiesIconsText(step.amenities))
        }

        if (!step.is_start_stop && step.waypoint_quadhashs.some(x => quadhashs.indexOf(x) === -1)) {
          for (const quadhash of step.waypoint_quadhashs) {
            if (quadhashs.indexOf(quadhash) === -1) {
              quadhashs.push(quadhash)
            }
          }
          stepLabelUid.push(uid)
          markerUid.push(uid)
          stepLabel.push(step.title)
        }
      }

      if (trail_id > 1) {
        const prevTrail = route.trails[trail_id - 2]
        const prevTrailStopUid = `${prevTrail.trail_id}-${prevTrail.steps_count}`
        startStepUid.push(prevTrailStopUid)
        startStepUid.splice(startStepUid.indexOf(`${trail_id}-1`), 1)
        startStopStepUid.splice(startStopStepUid.indexOf(prevTrailStopUid), 1, `${trail_id}-1`)
        markerUid.push(`${trail_id}-1`)
      }

      if (stepLabelUid.length > 0) {
        map.setFilter('step-label', ['in', ['get', 'step_uid'], ['literal', stepLabelUid]], options)
        map.setLayoutProperty('step-label', 'text-field', buildiMatchExpression(stepLabelUid, stepLabel, ''), options)

        map.setFilter('step-distance', ['in', ['get', 'step_uid'], ['literal', stepDistanceLabelUid]], options)
        map.setLayoutProperty('step-distance', 'text-field', buildiMatchExpression(stepDistanceLabelUid, stepDistanceLabel, ''), options)
        map.setLayoutProperty('step-distance', 'text-offset', ['interpolate', ['linear'], ['zoom'],
          13, buildiMatchExpression(stepDistanceOffsetUid, stepDistanceOffset, ['literal', [1, 2]]),
          14, ['literal', [1, 0]]], options)

        map.setFilter('step-icon', ['in', ['get', 'step_uid'], ['literal', iconUid]], options)
        map.setLayoutProperty('step-icon', 'text-field', buildiMatchExpression(iconUid, iconLabel, ''), options)
      }

      for (const layer of ['step-label', 'step-distance', 'step-icon']) {
        map.setLayoutProperty(
          layer,
          'visibility',
          (hasFeatures || stepLabelUid.length === 0) ? 'none' : 'visible'
        )
      }
    } else {
      for (const layer of ['step-label', 'step-distance', 'step-icon']) {
        map.setLayoutProperty(
          layer,
          'visibility',
          'none'
        )
      }
    }
    map.setFilter('trail-start-stop-marker', ['in', ['get', 'step_uid'], ['literal', startStopStepUid]], options)
    map.setFilter('trail-start-stop-label', ['in', ['get', 'step_uid'], ['literal', startStopStepUid]], options)
    map.setLayoutProperty('trail-start-stop-label', 'text-field', buildiMatchExpression(startStopStepUid, startStopLabel, ''), options)
    map.setFilter('step-marker', ['!', ['in', ['get', 'step_uid'], ['literal', startStepUid]]], options)
    map.setFilter('place', ['!',
      ['in', ['get', 'quadhash'], ['literal', quadhashs]]
    ], options)
    map.setPaintProperty('place', 'text-opacity', 0.7)
  }, [map, routeMapSettings, route, steps, trail_id, offsetDistance, unit, zoom, hasFeatures])

  useEffect(() => {
    if (!map || !routeMapSettings) return
    const layers = ['icon', 'icon-low-zoom', 'place', 'step-icon']
    for (const layer of layers) {
      !!map.getLayer(layer) && map.setLayoutProperty(layer, 'visibility', hasFeatures ? 'none' : 'visible')
    }
  }, [map, routeMapSettings, hasFeatures])

  useEffect(() => {
    if (!map || !routeMapSettings) return
    if (trail_highlight_id) {
      map.setFilter('trail-highlight', ['==', 'trail_id', parseInt(trail_highlight_id)])
      map.setLayoutProperty('trail-highlight', 'visibility', 'visible')
    } else {
      map.setLayoutProperty('trail-highlight', 'visibility', 'none')
    }
  }, [map, routeMapSettings, trail_highlight_id])

  if (routeMapSettings && route) return null

  return <Loading/>
})

export default RouteLayer
